import React, { useEffect, useMemo, useState } from 'react'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import useDistanceDisplay from 'hooks/useDistanceDisplay'
import BodyText from 'LuxKit/Typography/BodyText'

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'distance')?.video
const SLIDE_ID = 'distance' as const

function getHeading(hasTravelled: boolean, isFarTraveller: boolean, useTotalsFallback: boolean): string {
  if (useTotalsFallback) return 'In 2025, Luxury Escapes travellers journeyed'
  if (!hasTravelled) return 'In 2025, Luxury Escapes travellers journeyed'
  if (isFarTraveller) return 'You journeyed far and wide with us'
  return 'You proved the best escapes are closest to home'
}

function DistanceSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)
  const [animate, setAnimate] = useState(!SLIDE_VIDEO) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data
  const hasTravelled = !!(data?.stats.citiesVisited?.length && data.stats.totalDistanceKm > 0)
  const useTotalsFallback = isTotalsOnlyMode || !hasTravelled
  const totalDistanceKm = totalsData?.stats.totalDistanceKm ?? 0
  const travelledDistanceKm = useTotalsFallback ? totalDistanceKm : (data?.stats.totalDistanceKm ?? 0)
  const { displayDistance, unitLabel } = useDistanceDisplay(travelledDistanceKm)
  const useCompactFormat = displayDistance >= 1_000_000

  // Start animation when video is ready (only when this is the current slide)
  useEffect(() => {
    if (SLIDE_VIDEO && isVideoReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isVideoReady, isCurrentSlide])

  // Memoize background to prevent recreation
  const videoBackground = useMemo(() => {
    if (!SLIDE_VIDEO) return undefined
    return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
  }, [isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const isFarTraveller = (data?.stats.totalDistanceKm ?? 0) > 200
  const heading = getHeading(hasTravelled, isFarTraveller, useTotalsFallback)

  return (
    <SlideBase background={videoBackground}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">Total Distance Travelled</Caption>
            <Heading variant="heading5"><b>{heading}</b></Heading>
          </Group>
          <Heading variant="heading1"><i><FormatDecimal value={displayDistance} notation={useCompactFormat ? 'compact' : 'standard'} compactDisplay="long" maximumFractionDigits={0} /> {unitLabel}</i></Heading>
          {isTotalsOnlyMode && <BodyText variant="medium">
            That's 55 thousand trips around the earth – but less than halfway to Pluto!
          </BodyText>}
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(DistanceSlide)
